import React, { useState, useEffect } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import { LogOut } from 'lucide-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import MusicPlayer from './components/MusicPlayer';
import RandomMessage from './components/RandomMessage';
import Celebration from './components/Celebration';
import CosmicLoveBackground from './components/CosmicLoveBackground';
import CounterCard from './components/CounterCard';
import MiniGamesModal from './components/MiniGamesModal';
import GothicHeart from './components/GothicHeart';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

function MainContent({ onGamesChange }) {
  const { isAuthenticated } = useAuth();
  const [timeElapsed, setTimeElapsed] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [showSurprise, setShowSurprise] = useState(false);
  const [surpriseFading, setSurpriseFading] = useState(false);
  const [showMiniGames, setShowMiniGames] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    if (onGamesChange) onGamesChange(showMiniGames);
  }, [showMiniGames, onGamesChange]);

  useEffect(() => {
    setFadeIn(true);
  }, []);

  useEffect(() => {
    const startDate = new Date('2025-10-05T00:00:00');

    const calculateTime = () => {
      const now = new Date();
      const diff = now - startDate;

      if (diff < 0) {
        setTimeElapsed({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeElapsed({ days, hours, minutes, seconds });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleSurpriseClick = () => {
    setShowSurprise(true);
    setSurpriseFading(false);
    setTimeout(() => setSurpriseFading(true), 3500);
    setTimeout(() => { setShowSurprise(false); setSurpriseFading(false); }, 4200);
  };

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <>
      {/* Açılış Bölümü */}
      <div className={`hero-section ${fadeIn ? 'fade-in' : ''}`}>
        <h1 className="main-title">Bizim Hikayemiz...</h1>
        <p className="subtitle">5 Ekim 2025 – Her şeyin başladığı gün</p>
      </div>

      {/* Canlı 3D Sayaç */}
      <div className={`counter-section ${fadeIn ? 'fade-in-delayed' : ''}`}>
        <p className="counter-title">Seni çooook seviyorumm</p>
        
        <div className="counter-grid">
          <CounterCard value={timeElapsed.days} label="Gün" />
          <CounterCard value={timeElapsed.hours} label="Saat" />
          <CounterCard value={timeElapsed.minutes} label="Dakika" />
          <CounterCard value={timeElapsed.seconds} label="Saniye" />
        </div>

        <p className="counter-subtitle">HER ŞEYDEN VE HERKESTEN DAHAA ÇOOOOK</p>
      </div>

      {/* Duygusal Metin Bölümü */}
      <div className={`emotional-section ${fadeIn ? 'fade-in-more-delayed' : ''}`}>
        <p className="emotional-text">
          Bu sayaç sadece zamanı göstermiyor.<br />
          Her saniye seni ne kadar çok sevdiğimi hatırlatıyor.
        </p>
      </div>

      {/* Rastgele Mesaj Butonu */}
      <RandomMessage />

      {/* Sürpriz Buton */}
      <div className={`surprise-section ${fadeIn ? 'fade-in-most-delayed' : ''}`}>
        <Button 
          onClick={handleSurpriseClick}
          className="surprise-button"
          variant="outline"
        >
          Bu süre neden hiç durmuyor?
        </Button>

        {showSurprise && (
          <div className={`surprise-message ${surpriseFading ? 'fade-out' : 'fade-in'}`}>
            <p>Çünkü sana olan aşkım asla son bulmayacak.</p>
          </div>
        )}
      </div>

      {/* Sol altta süzülen sihirli aşk kalbi (Mini Oyunlar) */}
      {!showMiniGames && <GothicHeart onClick={() => setShowMiniGames(true)} />}

      {/* Kalbe tıklayınca açılan Mini Oyunlar (Wordle & Yılan) */}
      {showMiniGames && <MiniGamesModal onClose={() => setShowMiniGames(false)} />}
    </>
  );
}

function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Login />;
  return children;
}

function AppShell({ children, hideUI }) {
  const { logout } = useAuth();
  const [isGameActive, setIsGameActive] = useState(false);

  return (
    <div className="App">
      <CosmicLoveBackground />
      <Celebration />
      {!hideUI && <MusicPlayer />}
      {!hideUI && !isGameActive && (
        <button onClick={logout} className="logout-button" aria-label="Çıkış yap / PIN Ekranı" title="PIN Ekranına Dön">
          <LogOut className="logout-icon" />
        </button>
      )}
      <div className="container">
        {React.isValidElement(children)
          ? React.cloneElement(children, { onGamesChange: setIsGameActive })
          : children}
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    const purgeNetlify = () => {
      const selectors = [
        '#nl-badge-frame', '#nl-hud-frame', 'iframe[id*="nl-"]', 'iframe[id*="badge"]',
        'iframe[title*="Netlify" i]', 'iframe[title*="Powered by Netlify" i]',
        'iframe[src*="netlify" i]', 'iframe[srcdoc*="netlify" i]', 'iframe[srcdoc*="Netlify"]',
        'a[href*="netlify.com" i]', 'a[href*="netlify.app" i]', '[id*="nl-badge"]', '[class*="nl-badge"]',
        'netlify-drawer', '.netlify-badge', '[data-netlify-badge]', '[data-nf-variant]'
      ];
      selectors.forEach(sel => {
        try {
          document.querySelectorAll(sel).forEach(el => el.remove());
        } catch (e) {}
      });
    };
    purgeNetlify();
    const interval = setInterval(purgeNetlify, 800);
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppShell>
                  <MainContent />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Analytics />
      <SpeedInsights />
    </AuthProvider>
  );
}

export default App;
